import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AliasService } from './alias.service';
import { AuthenticationService } from './authentication.service';

/**
 * Shared `startSession -> re-confirm /user/me` orchestration, extracted so
 * both alias entry points — the header's "Alias User" picker
 * (`AliasVendorPickerDialogComponent`) and the Management -> Users row button
 * (`ManageUsersComponent`) — start a session the same way instead of each
 * inlining its own copy.
 *
 * A successful `startSession` only sets the alias cookie server-side; it
 * does not, by itself, update the cached user this app already holds. Both
 * callers are about to hard-reload into the vendor's `/dashboard`, and
 * `RoleGuard` there reads `getStoredUser()` synchronously on that fresh
 * load — so `me()` is re-run here, after `startSession` resolves, to
 * re-store the aliased VENDOR profile before the reload happens. A failed
 * `me()` is swallowed (`catchError` -> `of(null)`): the alias session has
 * already started server-side at that point, so stranding the caller here
 * wouldn't undo it, only delay the guard's own follow-up `/me` call — the
 * caller should reload anyway. A failed `startSession()` itself is NOT
 * swallowed: it propagates as an error, since no alias session exists yet.
 *
 * This service deliberately does not reload the page — that stays a
 * property of each caller (kept behind a protected, spyable method; see
 * `AliasVendorPickerDialogComponent.reloadIntoVendorPortal`), so this class
 * stays unit-testable with plain spies/`HttpTestingController`.
 */
@Injectable({
  providedIn: 'root',
})
export class AliasSessionStarter {
  constructor(
    private aliasService: AliasService,
    private authService: AuthenticationService
  ) {}

  start(vendorId: string): Observable<unknown> {
    return this.aliasService
      .startSession(vendorId)
      .pipe(switchMap(() => this.authService.me().pipe(catchError(() => of(null)))));
  }
}
